import PinyinMatch from "pinyin-match";
import { getBookName } from "./edition";
import { ZH_BOOK_ALIASES } from "./zhBookAliases";

const VERSES_PER_PAGE = 12;

export const escapeRegExp = (value) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const toPlainText = (text) =>
  String(text || "")
    .replace(/\r?\n+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const parseVerses = (content) => {
  if (!content) return [];
  const verses = [];
  String(content)
    .split(/\r?\n/)
    .forEach((line) => {
      const match = line.trim().match(/^(\d+)\s*(.*)$/);
      if (!match) return;
      verses.push({
        number: Number(match[1]),
        text: (match[2] || "").trim(),
      });
    });
  return verses;
};

const parseQuery = (rawTerm) => {
  const term = rawTerm.trim();
  const ref =
    term.match(/^(.+?)\s*(\d+)\s*[:：]\s*(\d+)\s*节?$/) ||
    term.match(/^(.+?)\s*第?\s*(\d+)\s*章\s*第?\s*(\d+)\s*节?$/) ||
    term.match(/^(.+?)\s+(\d+)\s*[:：]\s*(\d+)$/);
  if (ref) {
    return {
      term,
      bookQuery: ref[1].trim(),
      chapter: Number(ref[2]),
      verse: Number(ref[3]),
    };
  }
  const chapterOnly =
    term.match(/^(.+?)\s*第\s*(\d+)\s*章$/) ||
    term.match(/^(.+?)\s*(\d+)\s*章$/) ||
    term.match(/^([^0-9\s]+)\s*(\d+)$/) ||
    term.match(/^(.+?)\s+(\d+)$/);
  if (chapterOnly && Number.isFinite(Number(chapterOnly[2]))) {
    return {
      term,
      bookQuery: chapterOnly[1].trim(),
      chapter: Number(chapterOnly[2]),
      verse: null,
    };
  }
  return { term, bookQuery: term, chapter: null, verse: null };
};

const resolveBookNames = (query, bookNames) => {
  if (!query) return [];
  const lower = query.toLowerCase();
  const exact = bookNames.filter(
    (name) => name === query || name.toLowerCase() === lower
  );
  if (exact.length) return exact;

  const aliases = ZH_BOOK_ALIASES[query] || ZH_BOOK_ALIASES[query.toLowerCase()];
  if (aliases) {
    return aliases.filter((name) => bookNames.includes(name));
  }

  const starts = bookNames.filter(
    (name) => name.startsWith(query) || name.toLowerCase().startsWith(lower)
  );
  if (starts.length) return starts;

  return bookNames.filter(
    (name) => name.includes(query) || name.toLowerCase().includes(lower)
  );
};

const pinyinHits = (text, term) => {
  if (!text || !term) return false;
  try {
    return Boolean(PinyinMatch.match(text, term));
  } catch (error) {
    return false;
  }
};

const compareCanon = (aId, bId) => {
  const [aBook, aChapter] = String(aId).split("-").map(Number);
  const [bBook, bChapter] = String(bId).split("-").map(Number);
  if (aBook !== bBook) return aBook - bBook;
  return aChapter - bChapter;
};

export const searchBible = ({
  chapters,
  term,
  usesPinyin = false,
  chapterTitles = {},
}) => {
  const trimmed = (term || "").trim();
  if (!trimmed) {
    return { books: [], headingHits: [], verseHits: [], query: null };
  }

  const query = parseQuery(trimmed);
  const searchTerm = query.term;
  const lowerTerm = searchTerm.toLowerCase();
  const bookNames = [];
  const seenBooks = new Set();
  const chaptersByBook = new Map();

  chapters.forEach((chapter) => {
    const book = getBookName(chapter);
    if (!seenBooks.has(book)) {
      seenBooks.add(book);
      bookNames.push(book);
    }
    if (!chaptersByBook.has(book)) {
      chaptersByBook.set(book, []);
    }
    chaptersByBook.get(book).push(chapter);
  });

  const matchedBookNames = new Set(resolveBookNames(query.bookQuery, bookNames));

  const altNameToZh = new Map();
  chapters.forEach((chapter) => {
    if (chapter.altBookName) {
      altNameToZh.set(chapter.altBookName, getBookName(chapter));
    }
  });
  resolveBookNames(query.bookQuery, Array.from(altNameToZh.keys())).forEach(
    (altName) => {
      const zhName = altNameToZh.get(altName);
      if (zhName) matchedBookNames.add(zhName);
    }
  );

  if (usesPinyin && !query.chapter) {
    bookNames.forEach((name) => {
      if (pinyinHits(name, query.bookQuery)) {
        matchedBookNames.add(name);
      }
    });
  }

  const books = [];
  matchedBookNames.forEach((name) => {
    const list = chaptersByBook.get(name) || [];
    const isExact = name === query.bookQuery || name.startsWith(query.bookQuery);
    const isAlias = Boolean(ZH_BOOK_ALIASES[query.bookQuery]?.includes(name));
    const isPinyin = !isExact && !isAlias && usesPinyin && pinyinHits(name, query.bookQuery);
    books.push({
      book: name,
      chapterCount: list.length,
      firstChapterId: list[0]?.id,
      reason: isPinyin ? "pinyin" : isAlias || isExact ? "name" : "contains",
    });
  });

  books.sort((a, b) => {
    const rank = { name: 0, pinyin: 1, contains: 2 };
    if (rank[a.reason] !== rank[b.reason]) {
      return rank[a.reason] - rank[b.reason];
    }
    return compareCanon(a.firstChapterId, b.firstChapterId);
  });

  const headingHits = [];
  const verseMap = new Map();

  const pushVerse = (chapter, verse, extra = {}) => {
    const key = chapter.id;
    const current = verseMap.get(key) || {
      chapter,
      book: getBookName(chapter),
      verses: [],
    };
    if (!current.verses.some((item) => item.number === verse.number)) {
      current.verses.push({
        number: verse.number,
        text: verse.text,
        ...extra,
      });
    }
    verseMap.set(key, current);
  };

  chapters.forEach((chapter) => {
    const book = getBookName(chapter);
    const title = chapter.title.replace("# ", "");
    const heading = chapterTitles[chapter.id] || "";
    const titleHit =
      title.toLowerCase().includes(lowerTerm) ||
      heading.toLowerCase().includes(lowerTerm) ||
      (usesPinyin && (pinyinHits(title, searchTerm) || pinyinHits(heading, searchTerm)));

    const requestedBook =
      !query.chapter ||
      matchedBookNames.has(book) ||
      resolveBookNames(query.bookQuery, [book]).length > 0;

    if (query.chapter && requestedBook && chapter.chapterNumber === query.chapter) {
      if (query.verse) {
        const verses = parseVerses(chapter.content);
        const target = verses.find((verse) => verse.number === query.verse);
        if (target) {
          pushVerse(chapter, target, { pinned: true });
        }
      } else {
        headingHits.push({
          chapter,
          book,
          heading,
          reason: "reference",
        });
      }
    } else if (!query.chapter && titleHit && !matchedBookNames.has(book)) {
      headingHits.push({
        chapter,
        book,
        heading,
        reason:
          heading.toLowerCase().includes(lowerTerm) ||
          (usesPinyin && pinyinHits(heading, searchTerm))
            ? "heading"
            : "title",
      });
    } else if (!query.chapter && titleHit && heading.toLowerCase().includes(lowerTerm)) {
      headingHits.push({
        chapter,
        book,
        heading,
        reason: "heading",
      });
    }

    if (query.chapter && query.verse) {
      return;
    }

    const compactTerm = searchTerm.replace(/\s/g, "");
    if (compactTerm.length < 2) {
      return;
    }

    const verses = parseVerses(chapter.content);
    verses.forEach((verse) => {
      if (verse.text.toLowerCase().includes(lowerTerm)) {
        pushVerse(chapter, verse);
      }
    });
    parseVerses(chapter.altContent || "").forEach((verse) => {
      if (verse.text.toLowerCase().includes(lowerTerm)) {
        pushVerse(chapter, verse);
      }
    });
  });

  headingHits.sort((a, b) => compareCanon(a.chapter.id, b.chapter.id));

  const verseHits = Array.from(verseMap.values()).map((item) => {
    item.verses.sort((a, b) => {
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;
      return a.number - b.number;
    });
    return item;
  });

  verseHits.sort((a, b) => {
    if (b.verses.length !== a.verses.length) {
      return b.verses.length - a.verses.length;
    }
    return compareCanon(a.chapter.id, b.chapter.id);
  });

  return { books, headingHits, verseHits, query };
};

export const paginate = (items, page, pageSize = VERSES_PER_PAGE) => {
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const safePage = Math.min(Math.max(page, 1), totalPages);
  return {
    items: items.slice((safePage - 1) * pageSize, safePage * pageSize),
    totalPages,
    page: safePage,
    pageSize,
  };
};

export const highlightText = (text, term) => {
  if (!text || !term) return text;
  const needle = term.trim();
  if (!needle) return text;
  const source = String(text);
  const regex = new RegExp(escapeRegExp(needle), "ig");
  const parts = [];
  let lastIndex = 0;
  let match;
  let index = 0;
  while ((match = regex.exec(source))) {
    parts.push(source.slice(lastIndex, match.index));
    parts.push({ key: `h-${index}`, text: match[0] });
    lastIndex = match.index + match[0].length;
    index += 1;
    if (index > 20) break;
  }
  parts.push(source.slice(lastIndex));
  return parts;
};
